import TileLayer from '@arcgis/core/layers/TileLayer';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import { BaseLayerConfig, JepBasemap } from '@jeppesen-foreflight/jmkw-types';

// AuthenticatedLayerConfig objects had their domain prefixes removed and are added from the environment config
// during initialization in the BasemapRegistryConfig
export const BASEMAP_LAYER_CONFIGS: Record<string, BaseLayerConfig> = {
  'jmkw-dark-basemap': {
    url: '/arcgis/rest/services/Hosted/JMKW_Basemap_Dark/VectorTileServer',
    layer_id: 'jmkwDarkLayer',
    title: 'Dark',
    type: VectorTileLayer,
    theme: 'dark'
  } as const satisfies BaseLayerConfig,
  'jmkw-light-basemap': {
    url: '/arcgis/rest/services/Hosted/JMKW_Basemap_Light/VectorTileServer',
    layer_id: 'jmkwLightLayer',
    title: 'Light',
    type: VectorTileLayer,
    theme: 'light'
  } as const satisfies BaseLayerConfig,
  'usgs-imagery-basemap': {
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer',
    layer_id: 'usgsImageryLayer',
    title: 'USGS Imagery',
    type: TileLayer,
    theme: 'light'
  } as const satisfies BaseLayerConfig,
  'usgs-relief-basemap': {
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSShadedReliefOnly/MapServer',
    layer_id: 'usgsReliefLayer',
    title: 'USGS Relief',
    type: TileLayer,
    theme: 'light'
  } as const satisfies BaseLayerConfig,
  'usgs-hydro-cached': {
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSHydroCached/MapServer',
    layer_id: 'usgsHydroCached',
    title: 'USGS Hydro Cached',
    type: TileLayer,
    theme: 'light'
  } as const satisfies BaseLayerConfig,
  'usgs-topo-basemap': {
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer',
    layer_id: 'usgsTopographical',
    title: 'USGS Topographical',
    type: TileLayer,
    theme: 'light'
  } as const satisfies BaseLayerConfig
} as const;

export type BasemapKey = keyof typeof BASEMAP_LAYER_CONFIGS | string;

// All available basemaps including user-provided ones - this is called "AvailableBasemaps" because it is meant for consumption by the implementation team.
export interface AvailableBasemaps {
  'osm-basemap': JepBasemap;
  'usgs-imagery-basemap'?: JepBasemap;
  'usgs-relief-basemap'?: JepBasemap;
  'usgs-topo-basemap'?: JepBasemap;
  'usgs-hydro-basemap'?: JepBasemap;
  'jmkw-dark-basemap'?: JepBasemap;
  'jmkw-light-basemap'?: JepBasemap;
  [key: string]: JepBasemap | undefined; // THIS IS THE USER PROVIDED ONES
}

export const INTERNAL_BASEMAP_IDS = new Set(Object.keys(BASEMAP_LAYER_CONFIGS));

export function isInternalBasemap(
  id: string
): id is keyof typeof BASEMAP_LAYER_CONFIGS {
  return INTERNAL_BASEMAP_IDS.has(id);
}

/**
 * Updates the BASEMAP_LAYER_CONFIGS with URLs from Purly service when matching keys exist.
 * This allows dynamic URL injection from the Purly service while maintaining the static configuration structure.
 *
 * Usage example:
 * ```typescript
 * // Get basemap URLs from Purly service
 * const purlyBasemaps = purlyService.getBasemapUrls();
 *
 * // Update configs with Purly URLs (only for matching keys)
 * const updatedConfigs = updateBasemapConfigsWithPurlyUrls(purlyBasemaps);
 *
 * // Use updatedConfigs instead of BASEMAP_LAYER_CONFIGS for basemap creation
 * ```
 *
 * @param purlyBasemaps Record of Purly signed URL info keyed by service ID (e.g., 'jmkw-dark-basemap')
 * @returns Updated basemap layer configs with Purly URLs where available
 */
export function updateBasemapConfigsWithPurlyUrls(
  purlyBasemaps: Record<
    string,
    import('@jeppesen-foreflight/jmkw-types').PurlySignedUrlInfo
  >
): Record<string, BaseLayerConfig> {
  const updatedConfigs = { ...BASEMAP_LAYER_CONFIGS };

  // Update URLs for basemaps that exist in both configs and Purly results
  Object.keys(updatedConfigs).forEach((key) => {
    const purlyUrl = purlyBasemaps[key];
    if (purlyUrl) {
      updatedConfigs[key] = {
        ...updatedConfigs[key],
        url: purlyUrl.url
      };
    }
  });

  return updatedConfigs;
}
